'use client';

import { useEffect } from 'react';

import nprogress from '@/lib/nprogress';

export type TransitionBarProps = {
    /**
     * To show the TopLoader at bottom.
     * @default false
     *
     */
    showAtBottom?: boolean;
};

type PushStateInput = [data: unknown, unused: string, url?: string | URL | null | undefined];

export const TransitionBar = ({ showAtBottom = false }: TransitionBarProps) => {
    // Check if to show at bottom
    const positionStyle = showAtBottom ? 'bottom: 0;' : 'top: 0;';

    useEffect(() => {
        nprogress.configure({
            easing: 'ease',
            speed: 200,
        });

        const handleAnchorClick = (event: MouseEvent) => {
            const targetElement = event.currentTarget as HTMLAnchorElement;
            const targetUrl = targetElement.href;
            const onlyHref = targetUrl.replace(location.origin, '');

            // Skip anchors with target="_blank"
            if (targetElement.target === '_blank') return;

            // Skip anchors with download attribute
            if (targetElement.hasAttribute('download')) return;

            // Skip external urls
            if (targetUrl && targetUrl.indexOf(location.origin) !== 0) return;

            // Skip anchors with rel="external"
            if (targetElement.rel === 'external') return;

            // Skip anchors with data-nprogress="ignore"
            if (targetElement.getAttribute('data-nprogress') === 'ignore') return;

            // Skip anchors with #, #! or javascript:void(0);
            if (['/#', '#', '/#!', '#!', 'javascript:void(0)'].includes(onlyHref)) return;

            // Skip empty anchor "href" or "href" attribute is not available
            if (!targetUrl || targetUrl === undefined) return;

            // Skip control/command+click
            if (event.metaKey || event.ctrlKey) return;

            const currentUrl = location.href;
            if (targetUrl !== currentUrl) {
                nprogress.start();
            }
        };

        // MutationObserver to handle dynamic anchor elements
        const handleMutation: MutationCallback = () => {
            const anchorElements = document.querySelectorAll('a');

            // Skip anchors with target="_blank" and anchors without href
            const validAnchorELes = Array.from(anchorElements).filter(
                anchor => anchor.href && anchor.target !== '_blank',
            );
            validAnchorELes.forEach(anchor => anchor.addEventListener('click', handleAnchorClick));
        };

        const mutationObserver = new MutationObserver(handleMutation);
        mutationObserver.observe(document, { childList: true, subtree: true });

        window.history.pushState = new Proxy(window.history.pushState, {
            apply: (target, thisArg, argArray: PushStateInput) => {
                nprogress.done();
                return target.apply(thisArg, argArray);
            },
        });

        return () => {
            // MutationObserver cleanup
            mutationObserver.disconnect();
        };
    }, []);

    return (
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        <style jsx global>
            {`
                #nprogress {
                    pointer-events: none;
                }
                #nprogress .bar {
                    background: var(--color-error);
                    position: fixed;
                    z-index: 9999;
                    ${positionStyle}right:0;
                    width: 100%;
                    height: 4px;
                }
            `}
        </style>
    );
};
